import React from 'react'
import { Button, CopyToClipboardIcon, ShareIcon } from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import { LINK_SOURCE } from '../../constants'
import { useAppDispatch } from '../../store/hooks'
import { showGlobalToast } from '../../store/slices/globalToastSlice'
import { addTrafficSourceParameter } from '../../utils/links/shareLink'
import { getGroupDetailPageVariant, tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'
import serverData from '../../utils/server-data'

import * as styles from './ShareGroupButtons.css'

interface Props {
  shareLink: string
  copySuccessfulMessage: string
  isAdmin: boolean
  isPayPalUser: boolean
  groupId: string
  isFromCreate?: boolean
}

const ShareGroupButtons = ({
  shareLink,
  isAdmin,
  isPayPalUser,
  copySuccessfulMessage,
  groupId,
  isFromCreate = false,
}: Props) => {
  const canShareUrl =
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ url: shareLink })
  const dispatch = useAppDispatch()

  const handleClickOnCopy = async () => {
    if (isFromCreate) {
      tracking(
        Events.p2p_pools_create_success_sheet_copy_link_pressed({
          source: serverData.trackingParameters.externalSource,
          page_variant: 'creator',
          pool_id: groupId,
        }),
      )
    } else {
      tracking(
        Events.p2p_pools_detail_share_pressed({
          source: serverData.trackingParameters.externalSource,
          web_share_button: 'copy',
          page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
          pool_id: groupId,
        }),
      )
    }

    navigator.clipboard.writeText(
      addTrafficSourceParameter(shareLink, isAdmin, LINK_SOURCE.COPY),
    )

    dispatch(
      showGlobalToast({
        message: copySuccessfulMessage,
        isSuccess: true,
      }),
    )
  }

  const handleClickOnShare = () => {
    if (isFromCreate) {
      tracking(
        Events.p2p_pools_create_success_sheet_share_pressed({
          source: serverData.trackingParameters.externalSource,
          page_variant: 'creator',
          pool_id: groupId,
        }),
      )
    }
    navigator.share({
      url: addTrafficSourceParameter(shareLink, isAdmin, LINK_SOURCE.NATIVE),
    })
  }

  return (
    <menu className={styles.shareActionList}>
      <li>
        <Button
          variant="secondary"
          data-testid="copyLinkButton"
          onClick={handleClickOnCopy}
          iconComponent={CopyToClipboardIcon}
        >
          <Message id="share.sheetCopyLink" />
        </Button>
      </li>
      {canShareUrl && (
        <li>
          <Button
            variant="secondary"
            data-testid="shareButton"
            onClick={handleClickOnShare}
            iconComponent={ShareIcon}
          >
            <Message id="share.sheetShare" />
          </Button>
        </li>
      )}
    </menu>
  )
}

export default ShareGroupButtons
